import type { AccessProfile, PermissionKey } from '../../api/schemas';
import type { PortalMode } from '../../lib/mode';

/**
 * What the owner can turn on and off for each profile. The keys and the
 * defaults mirror services/api/src/business-permissions.ts (the API is the
 * boundary; this file only names things for the screen and hides doors the
 * API would refuse anyway).
 */
export type PermissionInfo = { key: PermissionKey; label: string; hint: string };
export type PermissionGroup = { title: string; items: readonly PermissionInfo[] };

export const PERMISSION_GROUPS: readonly PermissionGroup[] = [
  {
    title: 'Agenda y clases',
    items: [
      { key: 'agenda', label: 'Ver toda la agenda', hint: 'Las reservas de todo el negocio.' },
      { key: 'asistencia', label: 'Marcar llegadas', hint: 'Llegó o no llegó, en clases y reservas.' },
      { key: 'clases', label: 'Crear y editar clases', hint: 'El horario de clases, cupos e instructores.' },
    ],
  },
  {
    title: 'Clientes',
    items: [{ key: 'clientes', label: 'Ver clientes y sus fichas', hint: 'Nivel, lesiones, notas e historial.' }],
  },
  {
    title: 'Caja',
    items: [
      { key: 'cobrar', label: 'Cobrar en caja', hint: 'Registrar cobros y aplicar ofertas.' },
      { key: 'pagos', label: 'Ver pagos', hint: 'El historial de cobros del negocio.' },
      { key: 'anular', label: 'Anular cobros', hint: 'Deshacer un cobro ya registrado.' },
      { key: 'facturas', label: 'Facturar', hint: 'Emitir, enviar y ver facturas.' },
    ],
  },
  {
    title: 'Negocio',
    items: [
      { key: 'finanzas', label: 'Ver finanzas y reportes', hint: 'Ingresos, gastos y reportes.' },
      { key: 'servicios', label: 'Editar servicios y precios', hint: 'Servicios, paquetes, ofertas y menú.' },
      { key: 'horario', label: 'Editar horarios y equipo', hint: 'Horario del negocio, del equipo y vacaciones.' },
      { key: 'negocio', label: 'Editar el negocio', hint: 'Perfil, fotos, datos fiscales y pagos en línea.' },
      { key: 'inteligencia', label: 'Usar Dale Agents', hint: 'Los informes y las recomendaciones de tus agentes con tus números.' },
    ],
  },
];

export const ALL_PERMISSIONS: readonly PermissionKey[] = PERMISSION_GROUPS.flatMap((group) =>
  group.items.map((item) => item.key),
);

export const PROFILES: readonly AccessProfile[] = ['admin', 'front_desk', 'coach', 'waiter', 'accountant'];

/** What a new person is when the owner does not say: the counter. */
export const DEFAULT_PROFILE: AccessProfile = 'front_desk';

export const PROFILE_LABEL: Record<AccessProfile, string> = {
  admin: 'Administración',
  front_desk: 'Recepción',
  coach: 'Coach',
  waiter: 'Mesero',
  accountant: 'Contador',
};

/**
 * One line on what each profile sees, under the role picker, so the owner chooses without a manual. The real
 * list of doors is the API's default for the profile (services/api/src/business-permissions.ts) and the owner
 * can change it in "Qué ve cada perfil".
 */
export const PROFILE_BLURB: Record<AccessProfile, string> = {
  admin: 'Ve y maneja todo el negocio. Solo tú cambias el plan y los accesos.',
  front_desk: 'Atiende el día: agenda, clientes, cobros y facturas.',
  coach: 'Ve su propia agenda y marca las llegadas de sus clientes.',
  waiter: 'Ve las reservas, marca llegadas y lleva las comandas y las cuentas de las mesas.',
  accountant: 'Lleva las finanzas, las facturas y los pagos: puede anotar gastos y emitir facturas. Nada de clientes ni de agenda.',
};

/**
 * The profiles worth offering a business: a waiter works in a restaurant, so everybody else is not asked about
 * one. `keep` holds on to a profile a person already has, so a row never loses its own choice.
 */
export function profilesForMode(mode: PortalMode | undefined, keep?: AccessProfile): readonly AccessProfile[] {
  return PROFILES.filter((profile) => profile !== 'waiter' || mode === 'restaurant' || profile === keep);
}

/** A coach always keeps their own classes and bookings; this line says so where it matters. */
export const COACH_AGENDA_HINT = 'Sin esto, cada coach ve solo sus clases y sus reservas.';

/** Returns a new list with `key` added or removed, in catalog order. */
export function withPermission(
  current: readonly PermissionKey[],
  key: PermissionKey,
  allowed: boolean,
): PermissionKey[] {
  const next = new Set(current);
  if (allowed) next.add(key);
  else next.delete(key);
  return ALL_PERMISSIONS.filter((candidate) => next.has(candidate));
}
